import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import "../Styles/LoanDetails.css";
import { toast } from "react-toastify";

import {
  Landmark,
  Wallet,
  TrendingUp,
  CalendarDays,
  FileText,
  Upload,
  Trash2,
  ChevronLeft,
  Clock3,
  CircleDollarSign,
} from "lucide-react";

const LoanDetails = () => {
  const { loanId } = useParams();

  const navigate = useNavigate();

  const [loan, setLoan] = useState(null);

  const [transactions, setTransactions] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [breakdown, setBreakdown] =
    useState(null);

  const [docs, setDocs] = useState([]);

  const [uploadingCat, setUploadingCat] =
    useState(null);

  /* ========================================
      FETCH DATA
  ======================================== */

  useEffect(() => {
    const fetchLoanDetails =
      async () => {
        try {
          const loanData =
            await api.getLoanById(
              loanId
            );

          if (loanData?.loan) {
            setLoan(loanData.loan);

            setTransactions(
              loanData.transactions ||
                []
            );

            const breakdownData =
              await api.getLoanBreakdown(
                loanId
              );

            setBreakdown(
              breakdownData
            );
          }
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      };

    fetchLoanDetails();
  }, [loanId]);

  /* ========================================
      DOCUMENTS
  ======================================== */

  useEffect(() => {
    const loadDocs = async () => {
      try {
        const list =
          await api.listLoanDocuments(
            loanId
          );

        setDocs(list || []);
      } catch (e) {
        console.error(e);
      }
    };

    loadDocs();
  }, [loanId]);

  const refreshDocs = async () => {
    const list =
      await api.listLoanDocuments(
        loanId
      );

    setDocs(list || []);
  };

  /* ========================================
      DELETE PAYMENT
  ======================================== */

  const handleDeletePayment =
    async (paymentId) => {
      if (
        window.confirm(
          "Delete this payment?"
        )
      ) {
        try {
          const success =
            await api.deleteLoanPayment(
              paymentId
            );

          if (success) {
            setTransactions((prev) =>
              prev.filter(
                (transaction) =>
                  transaction.payment_id !==
                  paymentId
              )
            );

            const updatedLoan =
              await api.getLoanById(
                loanId
              );

            if (updatedLoan?.loan) {
              setLoan(
                updatedLoan.loan
              );
            }

            toast.success(
              "Payment deleted."
            );
          }
        } catch (error) {
          console.error(error);

          toast.error(
            "Delete failed."
          );
        }
      }
    };

  /* ========================================
      DOCUMENTS
  ======================================== */

  const handleUpload = async (
    category,
    file
  ) => {
    if (!file) return;

    try {
      setUploadingCat(category);

      await toast.promise(
        api.uploadLoanDocument(
          loanId,
          file,
          { category }
        ),
        {
          pending:
            "Uploading document...",
          success:
            "Document uploaded.",
          error:
            "Upload failed.",
        }
      );

      await refreshDocs();
    } catch (e) {
      console.error(e);
    } finally {
      setUploadingCat(null);
    }
  };

  const handleDeleteDoc = async (
    docId
  ) => {
    if (
      !window.confirm(
        "Delete this document?"
      )
    )
      return;

    const ok =
      await api.deleteLoanDocument(
        docId
      );

    if (ok) {
      setDocs((prev) =>
        prev.filter(
          (d) => d.id !== docId
        )
      );

      toast.success(
        "Document deleted."
      );
    }
  };

  const handleViewDoc = async (
    doc
  ) => {
    try {
      const signed =
        await api.getSignedDocUrl(
          doc.id
        );

      const url = signed?.url;

      if (!url) {
        toast.error(
          "Unable to open document."
        );

        return;
      }

      window.open(
        url,
        "_blank",
        "noopener,noreferrer"
      );
    } catch (e) {
      console.error(e);

      toast.error(
        "Unable to open document."
      );
    }
  };

  const docsByCategory = (cat) =>
    docs.filter(
      (d) =>
        (
          d.category || ""
        ).toLowerCase() ===
        cat.toLowerCase()
    );

  /* ========================================
      DOCUMENT COMPONENT
  ======================================== */

  const DocumentSection = ({
    title,
    category,
  }) => {
    const items =
      docsByCategory(category);

    return (
      <div className="loanprofile-doc-card">

        <div className="loanprofile-doc-top">

          <div>

            <h3>{title}</h3>

            <span>
              {
                items.length
              }{" "}
              Files
            </span>

          </div>

          <label className="loanprofile-upload-btn">

            <Upload size={16} />

            {uploadingCat ===
            category
              ? "Uploading..."
              : "Upload"}

            <input
              type="file"
              style={{
                display: "none",
              }}
              onChange={(e) =>
                handleUpload(
                  category,
                  e.target.files?.[0]
                )
              }
            />

          </label>

        </div>

        {items.length === 0 ? (

          <div className="loanprofile-doc-empty">
            No documents uploaded.
          </div>

        ) : (

          <div className="loanprofile-doc-list">

            {items.map((doc) => (

              <div
                key={doc.id}
                className="loanprofile-doc-item"
              >

                <div className="loanprofile-doc-left">

                  <div className="loanprofile-doc-icon">
                    <FileText size={18} />
                  </div>

                  <div>

                    <h4>
                      {
                        doc.file_name
                      }
                    </h4>

                  </div>

                </div>

                <div className="loanprofile-doc-actions">

                  <button
                    onClick={() =>
                      handleViewDoc(
                        doc
                      )
                    }
                  >
                    View
                  </button>

                  <button
                    className="delete"
                    onClick={() =>
                      handleDeleteDoc(
                        doc.id
                      )
                    }
                  >
                    <Trash2
                      size={16}
                    />
                  </button>

                </div>

              </div>

            ))}

          </div>

        )}

      </div>
    );
  };



  const handleDeleteLoan = async (
  loanId
) => {
  const confirmed =
    window.confirm(
      "Delete this loan permanently?"
    );

  if (!confirmed) return;

  try {
    const success =
      await api.deleteLoan(
        loanId
      );

    if (success) {
      toast.success(
        "Loan deleted successfully."
      );

      navigate("/loan");
    }
  } catch (error) {
    console.error(error);

    toast.error(
      "Failed to delete loan."
    );
  }
};

const handleSendReminder =
  async () => {

    try {

      await api.sendLoanReminder(
        loan.loan_id
      );

      toast.success(
        "Reminder SMS sent."
      );

    } catch (error) {

      console.error(error);

      toast.error(
        "Failed to send reminder."
      );

    }
};

const handleSendOverdueReminder =
  async () => {

    try {

      await api.sendOverdueReminder(
        loan.loan_id
      );

      toast.success(
        "Overdue reminder SMS sent."
      );

    } catch (error) {

      console.error(error);

      toast.error(
        "Failed to send overdue reminder."
      );

    }
};

  /* ========================================
      LOADING
  ======================================== */

  if (loading) {
    return (
      <div className="loanprofile-loading">
        Loading loan profile...
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="loanprofile-loading">
        Loan not found.
      </div>
    );
  }

  /* ========================================
      JSX
  ======================================== */

  return (
    <div className="loanprofile-page-shell">

      {/* HERO */}

      <div className="loanprofile-hero-section">

        <div className="loanprofile-hero-left">

          <div className="loanprofile-hero-tag">
            <Landmark size={14} />
            RCS FINANCE
          </div>

          <h1>
            {loan.client_name}
          </h1>

          <p>
            Monitor loan
            performance,
            repayments,
            uploaded documents
            and client financial
            activity.
          </p>


<div className="loanprofile-client-details">

  <div className="loanprofile-detail-card">
    <span>Phone Number</span>
    <strong>{loan.client_phone || "N/A"}</strong>
  </div>

  <div className="loanprofile-detail-card">
    <span>Loan Type</span>
    <strong>{loan.loan_type}</strong>
  </div>

  <div className="loanprofile-detail-card">
    <span>Start Date</span>
    <strong>
      {loan.start_date
        ? new Date(loan.start_date).toLocaleDateString()
        : "N/A"}
    </strong>
  </div>

  <div className="loanprofile-detail-card">
    <span>End Date</span>
    <strong>
      {loan.end_date
        ? new Date(loan.end_date).toLocaleDateString()
        : "N/A"}
    </strong>
  </div>

  <div className="loanprofile-detail-card">
    <span>Status</span>
    <strong>{loan.status}</strong>
  </div>

  <div className="loanprofile-detail-card">
    <span>Loan Term</span>
    <strong>{loan.loan_term} Months</strong>
  </div>

</div>

          <div className="loanprofile-hero-actions">

            <button
              className="loanprofile-primary-btn"
              onClick={() =>
                navigate("/loan")
              }
            >
              <ChevronLeft
                size={18}
              />
              Back to Loans
            </button>
<button
  className="loan-sms-btn"
  onClick={() =>
    handleSendReminder()
  }
>
  Send Reminder
</button>

<button
  className="loan-overdue-btn"
  onClick={() =>
    handleSendOverdueReminder()
  }
>
  Overdue Reminder
</button>

<button
  className="loan-delete-btn"
  onClick={(e) => {
    e.stopPropagation();
    handleDeleteLoan(
      loan.loan_id
    );
  }}
>
  <Trash2 size={18} />
</button>
          </div>

        </div>

        <div className="loanprofile-hero-card">

          <div className="loanprofile-card-top">

            <div>

              <span>
                Loan Account
              </span>

              <h2>
                #{loanId}
              </h2>

            </div>

            <div
              className={`loanprofile-status-pill ${loan.status?.toLowerCase()}`}
            >
              {loan.status}
            </div>

          </div>

          <div className="loanprofile-balance-label">
            Remaining Balance
          </div>

          <div className="loanprofile-balance">
            $
            {loan.remaining_balance?.toLocaleString()}
          </div>

          <div className="loanprofile-mini-grid">

            <div>

              <span>
                Monthly
              </span>

              <strong>
                $
                {loan.monthly_payment?.toLocaleString()}
              </strong>

            </div>

            <div>

              <span>
                Interest
              </span>

              <strong>
                {
                  loan.interest_rate
                }
                %
              </strong>

            </div>

          </div>

        </div>

      </div>

      {/* SUMMARY */}

      <div className="loanprofile-summary-grid">

        <div className="loanprofile-summary-card">

          <div className="loanprofile-summary-icon teal">
            <Wallet size={22} />
          </div>

          <span>
            Principal
          </span>

          <h3>
            $
            {loan.loan_amount?.toLocaleString()}
          </h3>

        </div>

        <div className="loanprofile-summary-card">

          <div className="loanprofile-summary-icon gold">
            <TrendingUp
              size={22}
            />
          </div>

          <span>
            Total Interest
          </span>

          <h3>
            $
            {loan.total_interest?.toLocaleString()}
          </h3>

        </div>

        <div className="loanprofile-summary-card">

          <div className="loanprofile-summary-icon blue">
            <CircleDollarSign
              size={22}
            />
          </div>

          <span>
            Total Payable
          </span>

          <h3>
            $
            {loan.total_payable?.toLocaleString()}
          </h3>

        </div>

        <div className="loanprofile-summary-card">

          <div className="loanprofile-summary-icon red">
            <CalendarDays
              size={22}
            />
          </div>

          <span>
            Loan Term
          </span>

          <h3>
            {loan.loan_term} Months
          </h3>

        </div>

      </div>

      {/* BREAKDOWN */}

      <div className="loanprofile-section">

        <div className="loanprofile-section-header">

          <h2>
            Loan Breakdown
          </h2>

        </div>

        <div className="loanprofile-breakdown-grid">

          <div className="loanprofile-breakdown-card">

            <span>
              Remaining Principal
            </span>

            <h3>
              $
              {breakdown?.remainingPrincipal?.toLocaleString()}
            </h3>

          </div>

          <div className="loanprofile-breakdown-card">

            <span>
              Remaining Interest
            </span>

            <h3>
              $
              {breakdown?.remainingInterest?.toLocaleString()}
            </h3>

          </div>

        </div>

      </div>

      {/* DOCUMENTS */}

      <div className="loanprofile-section">

        <div className="loanprofile-section-header">

          <h2>
            Documents
          </h2>

        </div>

        <div className="loanprofile-doc-grid">

          <DocumentSection
            title="Identification"
            category="ID"
          />

          <DocumentSection
            title="Other Documents"
            category="Other"
          />

        </div>

      </div>

      {/* PAYMENTS */}

      <div className="loanprofile-section">

        <div className="loanprofile-section-header">

          <h2>
            Payment Timeline
          </h2>

        </div>

        {transactions.length ===
        0 ? (

          <div className="loanprofile-empty-payments">
            No payments have
            been made yet.
          </div>

        ) : (

          <div className="loanprofile-payment-timeline">

            {transactions.map(
              (
                transaction,
                index
              ) => (

                <div
                  key={index}
                  className="loanprofile-payment-item"
                >

                  <div className="loanprofile-payment-line" />

                  <div className="loanprofile-payment-dot">
                    <Clock3
                      size={14}
                    />
                  </div>

                  <div className="loanprofile-payment-card">

                    <div className="loanprofile-payment-left">

                      <h3>
                        Payment
                        Received
                      </h3>

                      <span>
                        {new Date(
                          transaction.payment_date
                        ).toLocaleDateString()}
                      </span>

                    </div>

                    <div className="loanprofile-payment-right">

                      <strong>
                        $
                        {transaction.payment_amount?.toLocaleString()}
                      </strong>

                      <button
                        onClick={() =>
                          handleDeletePayment(
                            transaction.payment_id
                          )
                        }
                      >
                        <Trash2
                          size={16}
                        />
                      </button>

                    </div>

                  </div>

                </div>

              )
            )}

          </div>

        )}

      </div>

    </div>
  );
};

export default LoanDetails;